import { useMutation } from '@tanstack/react-query'
import type { CreateDocumentData, Options, VerifyDocumentData } from '@workspace/api-backend'
import { createDocumentMutation, verifyDocumentMutation } from '@workspace/api-backend/react-query'
import { useState } from 'react'

interface UseDocumentUploadOptions {
  onSuccess?: (documentId: string, file: File) => void
  onError?: (error: Error) => void
}

export function useDocumentUpload(options?: UseDocumentUploadOptions) {
  const [isUploading, setIsUploading] = useState(false)

  const createMut = useMutation({
    ...createDocumentMutation(),
  })

  const verifyMut = useMutation({
    ...verifyDocumentMutation(),
  })

  const upload = async (file: File): Promise<string> => {
    setIsUploading(true)

    try {
      // Step 1: Create document and get presigned URL
      const createResponse = await createMut.mutateAsync({
        body: { fileName: file.name },
      } as Options<CreateDocumentData>)

      if (createResponse.status !== 'ok') {
        throw new Error('Failed to create upload URL')
      }

      const { id, uploadUrl, uploadFields } = createResponse.data

      // Step 2: Upload directly to S3
      const formData = new FormData()
      for (const [fieldKey, fieldValue] of Object.entries(uploadFields)) {
        formData.append(fieldKey, fieldValue)
      }
      formData.append('file', file)

      const uploadResponse = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
      })

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to storage')
      }

      // Step 3: Verify upload
      const verifyResponse = await verifyMut.mutateAsync({
        path: { id },
      } as Options<VerifyDocumentData>)

      if (verifyResponse.status !== 'ok') {
        throw new Error('Failed to verify upload')
      }

      options?.onSuccess?.(id, file)
      return id
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Upload failed')
      options?.onError?.(err)
      throw err
    } finally {
      setIsUploading(false)
    }
  }

  return {
    upload,
    isUploading,
  }
}
